<template>
  <div class="detail-list" :class="listClass">
    <div
      v-for="(item, index) in items"
      :key="index"
      class="detail-row"
      :class="{ 'detail-row--highlight': item.highlight }"
    >
      <span class="detail-label">{{ item.label }}</span>
      <span class="detail-value" :class="valueClass(item)">
        <slot :name="`value-${index}`" :item="item">
          {{ item.value }}
        </slot>
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DetailList',

  props: {
    items: {
      type: Array,
      required: true,
      validator: (items) => items.every(item => 'label' in item && 'value' in item)
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'card', 'compact'].includes(value)
    },
    dividers: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    listClass() {
      return [
        `detail-list--${this.variant}`,
        { 'detail-list--no-dividers': !this.dividers }
      ]
    }
  },

  methods: {
    valueClass(item) {
      const classes = []
      if (item.highlight) classes.push('detail-value--highlight')
      if (item.color) classes.push(`detail-value--${item.color}`)
      if (item.mono) classes.push('detail-value--mono')
      if (item.size) classes.push(`detail-value--${item.size}`)
      return classes
    }
  }
}
</script>

<style scoped>
.detail-list {
  display: flex;
  flex-direction: column;
}

/* Variants */
.detail-list--default {
  gap: var(--space-3, 0.75rem);
}

.detail-list--card {
  background: var(--bg-muted, #f4f4f5);
  border-radius: var(--radius-lg, 0.5rem);
  padding: var(--space-4, 1rem);
}

.detail-list--card .detail-row {
  padding: var(--space-2, 0.5rem) 0;
}

.detail-list--compact {
  gap: var(--space-2, 0.5rem);
}

/* Dividers */
.detail-list--default .detail-row:not(:last-child),
.detail-list--card .detail-row:not(:last-child) {
  padding-bottom: var(--space-3, 0.75rem);
  border-bottom: 1px solid var(--border-color-light, #f4f4f5);
}

.detail-list--card .detail-row:not(:last-child) {
  border-bottom-color: var(--border-color, #e4e4e7);
}

.detail-list--no-dividers .detail-row {
  padding-bottom: 0 !important;
  border-bottom: none !important;
}

/* Row */
.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4, 1rem);
}

.detail-row--highlight {
  padding-top: var(--space-3, 0.75rem);
  border-top: 1px solid var(--border-color, #e4e4e7);
}

/* Label */
.detail-label {
  font-size: var(--text-md, 0.875rem);
  color: var(--text-muted, #71717a);
  font-weight: var(--font-medium, 500);
  flex-shrink: 0;
}

.detail-list--compact .detail-label {
  font-size: var(--text-base, 0.8125rem);
}

/* Value */
.detail-value {
  font-size: var(--text-md, 0.875rem);
  font-weight: var(--font-semibold, 600);
  color: var(--text-primary, #18181b);
  text-align: right;
}

.detail-list--compact .detail-value {
  font-size: var(--text-base, 0.8125rem);
}

/* Value Modifiers */
.detail-value--highlight {
  font-weight: var(--font-bold, 700);
}

.detail-value--primary {
  color: var(--color-primary-500, #0ea5e9);
}

.detail-value--success {
  color: var(--color-success-500, #10b981);
}

.detail-value--error {
  color: var(--color-error-600, #dc2626);
}

.detail-value--warning {
  color: var(--color-warning-600, #d97706);
}

.detail-value--mono {
  font-family: var(--font-family-mono, monospace);
}

.detail-value--lg {
  font-size: var(--text-xl, 1rem);
}

.detail-value--xl {
  font-size: var(--text-2xl, 1.125rem);
}

/* Responsive */
@media (max-width: 576px) {
  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-1, 0.25rem);
  }

  .detail-value {
    text-align: left;
  }
}
</style>
